import { useState, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import RestoreIcon from '@mui/icons-material/Restore';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { PageHeader } from '../../components/common/PageHeader';
import {
  useGetDefaultPrompt,
  useUpdateDefaultPrompt,
  useGetDefaultPromptHistory,
  useGetOrgPrompt,
  useUpdateOrgPrompt,
  useDeleteOrgPrompt,
  useGetOrgPromptHistory,
  useRollbackDefaultPrompt,
  useRollbackOrgPrompt,
  getGetDefaultPromptQueryKey,
  getGetDefaultPromptHistoryQueryKey,
  getGetOrgPromptQueryKey,
  getGetOrgPromptHistoryQueryKey,
  useAdminListOrganizations,
} from '../../api/generated/generated';
import type { ContextToggles, PromptVersion, PromptType } from '../../api/generated';
import { TipTapEditor } from '../../components/ui/TipTapEditor';
import { ContextTogglesPanel } from '../../components/ui/ContextTogglesPanel';
import { PromptPreviewPanel } from '../../components/ui/PromptPreviewPanel';
import { VersionDiffDialog } from '../../components/ui/VersionDiffDialog';
import { getErrorMessage } from '../../api/client';

const PROMPT_TYPES = ['pre_call', 'post_call'] as const;
const PROMPT_LABELS: Record<string, string> = {
  pre_call: 'Pre-Call Research',
  post_call: 'Post-Call Grading',
};

const SYSTEM_DEFAULT = '__system_default__';

function toToggleRecord(
  toggles: ContextToggles | null | undefined
): Record<string, boolean> {
  if (!toggles) return { ...DEFAULT_TOGGLES };
  const result: Record<string, boolean> = { ...DEFAULT_TOGGLES };
  for (const [key, value] of Object.entries(toggles)) {
    if (value !== null && value !== undefined) {
      result[key] = value;
    }
  }
  return result;
}

const DEFAULT_TOGGLES: Record<string, boolean> = {
  include_user_context: true,
  include_org_context: true,
  include_prospect_context: true,
  include_transcript_context: true,
};

export function AdminPromptsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedOrg, setSelectedOrg] = useState(SYSTEM_DEFAULT);
  const promptType = (PROMPT_TYPES[tabIndex] ?? 'pre_call') as PromptType;

  const isOrgMode = selectedOrg !== SYSTEM_DEFAULT;
  const orgId = isOrgMode ? selectedOrg : '';

  // Data hooks
  const { data: defaultPromptResp, isLoading: defaultLoading } =
    useGetDefaultPrompt(promptType);
  const { data: defaultHistoryResp } = useGetDefaultPromptHistory(promptType);
  const { data: orgPromptResp, isLoading: orgLoading } = useGetOrgPrompt(
    orgId,
    promptType,
    { query: { enabled: !!orgId && !!promptType } }
  );
  const { data: orgHistoryResp } = useGetOrgPromptHistory(
    orgId,
    promptType,
    { query: { enabled: !!orgId && !!promptType } }
  );
  const { data: orgsResp } = useAdminListOrganizations({ limit: 100, offset: 0 });

  // Unwrap response envelopes (discriminate on status to narrow union types)
  const defaultPrompt = defaultPromptResp?.status === 200 ? defaultPromptResp.data : undefined;
  const defaultHistory = defaultHistoryResp?.status === 200 ? defaultHistoryResp.data : undefined;
  const orgPrompt = orgPromptResp?.status === 200 ? orgPromptResp.data : undefined;
  const orgHistory = orgHistoryResp?.status === 200 ? orgHistoryResp.data : undefined;

  // Mutation hooks — cache invalidation via onSuccess
  const updateDefault = useUpdateDefaultPrompt({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetDefaultPromptQueryKey(promptType),
        });
        queryClient.invalidateQueries({
          queryKey: getGetDefaultPromptHistoryQueryKey(promptType),
        });
      },
    },
  });
  const updateOrg = useUpdateOrgPrompt({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetOrgPromptQueryKey(orgId, promptType),
        });
        queryClient.invalidateQueries({
          queryKey: getGetOrgPromptHistoryQueryKey(orgId, promptType),
        });
      },
    },
  });
  const deleteOrg = useDeleteOrgPrompt({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetOrgPromptQueryKey(orgId, promptType),
        });
        queryClient.invalidateQueries({
          queryKey: getGetOrgPromptHistoryQueryKey(orgId, promptType),
        });
      },
    },
  });
  const rollbackDefault = useRollbackDefaultPrompt({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetDefaultPromptQueryKey(promptType),
        });
        queryClient.invalidateQueries({
          queryKey: getGetDefaultPromptHistoryQueryKey(promptType),
        });
      },
    },
  });
  const rollbackOrg = useRollbackOrgPrompt({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetOrgPromptQueryKey(orgId, promptType),
        });
        queryClient.invalidateQueries({
          queryKey: getGetOrgPromptHistoryQueryKey(orgId, promptType),
        });
      },
    },
  });

  // Local state
  const [content, setContent] = useState('');
  const [changelog, setChangelog] = useState('');
  const [contextToggles, setContextToggles] = useState<Record<string, boolean>>(DEFAULT_TOGGLES);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  const [restoreVersion, setRestoreVersion] = useState<number | null>(null);
  const [diffVersions, setDiffVersions] = useState<{
    old: PromptVersion | null;
    new: PromptVersion | null;
  }>({ old: null, new: null });
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  // Compute active prompt and history
  const activePrompt = isOrgMode ? orgPrompt : defaultPrompt;
  const activeHistory = isOrgMode ? orgHistory : defaultHistory;
  const isLoading = isOrgMode ? orgLoading : defaultLoading;
  const isSaving = updateDefault.isPending || updateOrg.isPending;

  // Sync editor content with active prompt
  useEffect(() => {
    if (isOrgMode && orgPrompt) {
      setContent(orgPrompt.content);
      setContextToggles(toToggleRecord(orgPrompt.context_toggles));
    } else if (isOrgMode && !orgPrompt && defaultPrompt) {
      // Org has no override — show default as starting point
      setContent(defaultPrompt.content);
      setContextToggles({ ...DEFAULT_TOGGLES });
    } else if (!isOrgMode && defaultPrompt) {
      setContent(defaultPrompt.content);
      setContextToggles(toToggleRecord(defaultPrompt.context_toggles));
    }
    setChangelog('');
  }, [isOrgMode, orgPrompt, defaultPrompt, promptType, selectedOrg]);

  const orgsData = orgsResp?.status === 200 ? orgsResp.data : undefined;
  const orgOptions = useMemo(
    () => orgsData?.organizations ?? [],
    [orgsData]
  );

  if (!user?.superAdmin) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Admin Prompts
        </Typography>
        <Alert severity="warning">Super admin access required.</Alert>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleSave = async () => {
    if (!changelog.trim()) return;
    try {
      if (isOrgMode) {
        await updateOrg.mutateAsync({
          orgId,
          promptType,
          data: { content, changelog },
        });
        setSnackbar({ open: true, message: 'Org prompt override saved', severity: 'success' });
      } else {
        await updateDefault.mutateAsync({
          promptType,
          data: { content, changelog },
        });
        setSnackbar({ open: true, message: 'Default prompt saved', severity: 'success' });
      }
      setChangelog('');
    } catch (error) {
      setSnackbar({ open: true, message: getErrorMessage(error), severity: 'error' });
    }
  };

  const handleRestore = async () => {
    if (restoreVersion === null) return;
    try {
      if (isOrgMode) {
        await rollbackOrg.mutateAsync({
          orgId,
          promptType,
          version: restoreVersion,
        });
      } else {
        await rollbackDefault.mutateAsync({
          promptType,
          version: restoreVersion,
        });
      }
      setSnackbar({
        open: true,
        message: `Restored to v${restoreVersion}`,
        severity: 'success',
      });
      setRestoreVersion(null);
    } catch (error) {
      setSnackbar({ open: true, message: getErrorMessage(error), severity: 'error' });
      setRestoreVersion(null);
    }
  };

  const handleResetToDefault = async () => {
    try {
      await deleteOrg.mutateAsync({ orgId, promptType });
      setSnackbar({
        open: true,
        message: 'Org override removed, reverted to default',
        severity: 'success',
      });
      setResetDialogOpen(false);
    } catch (error) {
      setSnackbar({ open: true, message: getErrorMessage(error), severity: 'error' });
      setResetDialogOpen(false);
    }
  };

  const handleToggleChange = (key: string, value: boolean) => {
    setContextToggles((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Box>
      <PageHeader title="Prompt Management" />
      {/* Org selector */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel>Scope</InputLabel>
          <Select
            value={selectedOrg}
            label="Scope"
            onChange={(e) => setSelectedOrg(e.target.value)}
          >
            <MenuItem value={SYSTEM_DEFAULT}>System Default</MenuItem>
            <Divider />
            {orgOptions.map((org) => (
              <MenuItem key={org.id} value={org.id}>
                {org.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {isOrgMode && orgPrompt && (
          <Button
            variant="outlined"
            color="warning"
            size="small"
            onClick={() => setResetDialogOpen(true)}
          >
            Reset to Default
          </Button>
        )}
        {isOrgMode && !orgPrompt && (
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            No override — using system default. Saving will create an org override.
          </Typography>
        )}
      </Box>
      {/* Prompt type tabs */}
      <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ mb: 3 }}>
        {PROMPT_TYPES.map((type) => (
          <Tab key={type} label={PROMPT_LABELS[type]} />
        ))}
      </Tabs>
      {/* Editor */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {PROMPT_LABELS[promptType]}
            {isOrgMode && orgPrompt && (
              <Typography
                component="span"
                variant="caption"
                sx={{
                  color: "info.main",
                  ml: 1
                }}>
                (org override v{orgPrompt.version})
              </Typography>
            )}
            {!isOrgMode && activePrompt && (
              <Typography
                component="span"
                variant="caption"
                sx={{
                  color: "text.secondary",
                  ml: 1
                }}>
                v{activePrompt.version}
              </Typography>
            )}
          </Typography>

          <TipTapEditor
            content={content}
            onChange={setContent}
            placeholder="Enter prompt instructions..."
          />

          <TextField
            fullWidth
            label="Changelog (required)"
            value={changelog}
            onChange={(e) => setChangelog(e.target.value)}
            size="small"
            sx={{ mt: 2, mb: 2 }}
            slotProps={{
              htmlInput: { maxLength: 500 }
            }}
          />

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving || !changelog.trim() || !content.trim()}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </CardContent>
      </Card>
      {/* Context Toggles + Preview side by side */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        <Card sx={{ flex: '1 1 300px' }}>
          <CardContent>
            <ContextTogglesPanel
              values={contextToggles}
              onChange={handleToggleChange}
            />
          </CardContent>
        </Card>
        <Box sx={{ flex: '2 1 400px' }}>
          <PromptPreviewPanel content={content} contextToggles={contextToggles} />
        </Box>
      </Box>
      {/* Version History */}
      {activeHistory && activeHistory.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Version History
            </Typography>
            <List dense>
              {activeHistory.map((version, index) => (
                <Box key={version.version}>
                  {index > 0 && <Divider />}
                  <ListItem
                    secondaryAction={
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          title="Restore this version"
                          onClick={() => setRestoreVersion(version.version)}
                        >
                          <RestoreIcon fontSize="small" />
                        </IconButton>
                        {index < activeHistory.length - 1 && (
                          <IconButton
                            size="small"
                            title="Compare with previous"
                            onClick={() =>
                              setDiffVersions({
                                old: activeHistory[index + 1] ?? null,
                                new: version,
                              })
                            }
                          >
                            <CompareArrowsIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={`v${version.version} — ${version.changelog}`}
                      secondary={`${new Date(version.updated_at).toLocaleDateString()} · ${version.updated_by}`}
                    />
                  </ListItem>
                </Box>
              ))}
            </List>
          </CardContent>
        </Card>
      )}
      {/* Restore confirmation dialog */}
      <Dialog open={restoreVersion !== null} onClose={() => setRestoreVersion(null)}>
        <DialogTitle>Restore Version</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to restore to version {restoreVersion}? This will create a new
            version with that content.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreVersion(null)}>Cancel</Button>
          <Button
            onClick={handleRestore}
            variant="contained"
            disabled={rollbackDefault.isPending || rollbackOrg.isPending}
          >
            Restore
          </Button>
        </DialogActions>
      </Dialog>
      {/* Reset to default dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Reset to Default</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will delete the organization's prompt override. The org will use the system
            default prompt. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleResetToDefault}
            color="error"
            variant="contained"
            disabled={deleteOrg.isPending}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>
      {/* Diff dialog */}
      <VersionDiffDialog
        open={diffVersions.old !== null && diffVersions.new !== null}
        onClose={() => setDiffVersions({ old: null, new: null })}
        oldVersion={diffVersions.old}
        newVersion={diffVersions.new}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
